/**
 * Lazy PostHog provider — renders children immediately and loads the
 * PostHogProvider once posthog-js has been dynamically imported.
 *
 * This keeps posthog-js out of the initial bundle while still supporting
 * the <PostHogProvider> wrapper needed by posthog-js/react hooks.
 */
import React, { useState, useEffect } from 'react';

interface LazyPostHogProviderProps {
  children: React.ReactNode;
}

export const LazyPostHogProvider: React.FC<LazyPostHogProviderProps> = ({ children }) => {
  const [wrapper, setWrapper] = useState<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Provider: React.ComponentType<any>;
    client: unknown;
  } | null>(null);

  useEffect(() => {
    const posthogKey = (import.meta.env.VITE_POSTHOG_KEY ?? '').trim();
    if (!posthogKey) return;

    let cancelled = false;

    const load = async () => {
      const [{ default: posthog }, { PostHogProvider }] = await Promise.all([
        import('posthog-js'),
        import('posthog-js/react'),
      ]);
      if (!cancelled) {
        setWrapper({ Provider: PostHogProvider, client: posthog });
      }
    };

    // Defer until idle so we don't block paint
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => { load(); });
    } else {
      setTimeout(() => { load(); }, 50);
    }

    return () => { cancelled = true; };
  }, []);

  if (wrapper) {
    const { Provider, client } = wrapper;
    return <Provider client={client}>{children}</Provider>;
  }

  // Render children immediately without waiting for PostHog
  return <>{children}</>;
};
